/**
 * Where the sun's shadows of buildings fall on one storey: a pure model (no pixi), drawn by
 * BuildingShadowLayer (building_shadow.ts).
 *
 * Everything solid above a storey's floor casts onto it: walls (as boxes standing on their own
 * storey), the floor slabs of the storeys above, and roofs (a pitched roof rising to its ridge). Each
 * caster's ground footprint is swept along the sun from its lowest to its highest point, so a shadow's
 * length is the caster's height above THIS storey -- a tower's walls cast three storeys' worth onto
 * the ground and one storey's worth onto its own top floor.
 *
 * A storey only receives where it has a surface: the ground everywhere, an upper storey where its
 * floor art is drawn (floor_art.ts: the art stops short of a hole's cells, so the hole looks wider than
 * they are), and on the roofs of the storey below wherever it has no floor of its own: a roof is what it
 * walks on there (a castle keep's roof, under a house built on it). An opening in a floor is not a surface, and it is also a gap in that slab, so the storey
 * below gets the sunlight through it. Clipping to the drawn floor is what keeps an upper storey's
 * shade from hanging over a hole in mid-air; the storey below draws that shade where it really lands.
 *
 * Heights are measured in the same pixels the storeys are drawn apart in (STOREY_RISE_PX), so the sun
 * here is the people's sun: a pixel of height moves the shadow `sun.x` across and `sun.y` down.
 */
import { pitchedRoofPiece } from "game-common/room/autotile_roof"
import type { WallShadow } from "game-common/room/autotile_wall"
import { drawnFloorRects, FloorRect } from "./floor_art"

const T = 32

/** One storey as a shadow sees it. Cells are "x,y" keys. */
export interface ShadowStorey {
    /** 0 is the ground. */
    floor: number
    /** The ground receives everywhere; an upper storey only on its `surface`. */
    ground: boolean
    /** Each wall's own cell (its parent). */
    walls: ReadonlySet<string>
    /** Where this storey can be stood on: its floor, without openings. */
    surface: ReadonlySet<string>
    /**
     * Floor cells whose art is not drawn, though their neighbours' art runs on into them as into floor: under an arch
     * cut over a gateway below, where the passage shows through. They take no shade.
     */
    undrawn?: ReadonlySet<string>
    /** This storey's floor as it shades the storeys below: no openings, no stairwells. */
    slab: ReadonlySet<string>
    /** Roof cells on this storey, and which of them are pitched. */
    roofs: ReadonlySet<string>
    pitched: ReadonlySet<string>
    /** The roofs of the storey below, which this storey stands on where it has no floor (drawn at their own height). */
    roofsBelow?: ReadonlySet<string>
    /** Walls whose material casts other than wall-stone does (a castle wall's thick body, merlons' free blocks). */
    profiles?: ReadonlyMap<string, WallShadow>
    /**
     * Where a parapet (merlons) stands on the wall of the storey below, with no floor: the cell takes shade all over,
     * on that wall's top, which is drawn there -- between the merlons, the crenels.
     */
    parapets?: ReadonlySet<string>
}

export interface BuildingShadowOptions {
    /** Ground displacement per pixel of height (the people's sun). */
    sun: { x: number; y: number }
    /** How far apart storeys stand (STOREY_RISE_PX). */
    rise: number
    /** How high a roof is drawn over its storey (ROOF_UPLIFT_PX). */
    roofLift: number
    /** Shade under a roof or the floor above. False: indoors stays lit, outside keeps its shadows. */
    indoors: boolean
}

/** A convex polygon as flat x, y pairs, in the receiving storey's own pixels. */
export type ShadowPolygon = number[]

/**
 * Where a wall stands inside its cell: the body of a north-south run spans these columns, an east-west
 * run is this deep and stands on the cell's bottom edge. Measured from wall-stone (timber-frame-wall
 * uses its geometry); the fence walls stand within a couple of pixels of it.
 */
export const WALL_BODY = { left: 10, right: 22, depth: 9 }
/**
 * A wall piece with no wall on any side (a merlon of a crenellated parapet, a lone post) is drawn as a
 * block nearly the cell's width, not a run's thin post: its shadow is as broad as its art.
 */
export const LONE_WALL = { left: 1, right: 31, depth: 12 }
/** How tall a wall's own art is (its top band sits this far above its footprint). */
export const WALL_TOP = 40

interface Caster { x0: number; y0: number; x1: number; y1: number; low: number; high: number }

const parse = (key: string): [number, number] => {
    const comma = key.indexOf(",")
    return [Number(key.slice(0, comma)), Number(key.slice(comma + 1))]
}

/** The rectangles a storey's walls stand on: a post on each wall's cell, joined to its east and north neighbours. */
export const wallFootprints = (walls: ReadonlySet<string>, profiles?: ReadonlyMap<string, WallShadow>): { x0: number; y0: number; x1: number; y1: number; cell: string }[] => {
    const out: { x0: number; y0: number; x1: number; y1: number; cell: string }[] = []
    walls.forEach(cell => {
        const [x, y] = parse(cell)
        // A block stands alone on its cell, joined to nothing.
        const block = profiles?.get(cell)?.block
        if (block) return out.push({ x0: x * T + block.left, y0: y * T + block.top, x1: x * T + block.right, y1: y * T + block.bottom, cell })
        const { left, right, depth } = profiles?.get(cell)?.body ?? WALL_BODY
        const n = walls.has(`${x},${y - 1}`), s = walls.has(`${x},${y + 1}`)
        const e = walls.has(`${x + 1},${y}`), w = walls.has(`${x - 1},${y}`)
        const bottom = y * T + T, top = bottom - depth
        if (!n && !s && !e && !w) {
            out.push({ x0: x * T + LONE_WALL.left, y0: bottom - LONE_WALL.depth, x1: x * T + LONE_WALL.right, y1: bottom, cell })
            return
        }
        // The open end of an east-west run is drawn out to the cell's edge.
        const run = !n && !s
        const x0 = run && e && !w ? 0 : left
        const x1 = run && w && !e ? T : right
        out.push({ x0: x * T + x0, y0: top, x1: x * T + x1, y1: bottom, cell })
        if (e) out.push({ x0: x * T + right, y0: top, x1: (x + 1) * T + left, y1: bottom, cell })
        if (n) out.push({ x0: x * T + left, y0: y * T, x1: x * T + right, y1: top, cell })
    })
    return out
}

/**
 * Whether cell (x, y), no wall itself, is a doorway: a gap of at most `span` cells in a run of walls along (dx, dy), the
 * walls either side ending the run there. A run the gap's line merely crosses (a corridor between two parallel walls,
 * like the ground between a castle's curtain and a tower) does not make one.
 */
const inDoorway = (walls: ReadonlySet<string>, x: number, y: number, dx: number, dy: number, span: number): boolean => {
    const wall = (cx: number, cy: number) => walls.has(`${cx},${cy}`)
    const ends = (cx: number, cy: number) => !(wall(cx + dy, cy + dx) && wall(cx - dy, cy - dx))
    for (let a = 1; a <= span; a++) {
        if (!wall(x - dx * a, y - dy * a)) continue
        if (!ends(x - dx * a, y - dy * a)) return false
        for (let b = 1; a + b - 1 <= span; b++) if (wall(x + dx * b, y + dy * b)) return ends(x + dx * b, y + dy * b)
        return false
    }
    return false
}

/**
 * How far into cell (x, y) the wall standing on it reaches, in the cell's own pixels: a column's body spans
 * `left`..`right` (a run joined east or west reaches that edge) and begins `top` below the cell's top edge. A doorway, the
 * gap in a run between two of its walls, counts as the run. Undefined where no wall stands, and for a free block (it stands
 * clear of every edge). Past these faces a wall's cell is outside the building, though the floor above covers the cell.
 */
export const wallExtent = (walls: ReadonlySet<string>, profiles: ReadonlyMap<string, WallShadow> | undefined, x: number, y: number):
    { left: number; right: number; top: number } | undefined => {
    const has = (cx: number, cy: number) => walls.has(`${cx},${cy}`)
    const n = has(x, y - 1), s = has(x, y + 1), e = has(x + 1, y), w = has(x - 1, y)
    if (!has(x, y)) {
        if (inDoorway(walls, x, y, 0, 1, 1)) {
            const { left, right } = profiles?.get(`${x},${y - 1}`)?.body ?? WALL_BODY
            return { left, right, top: 0 }
        }
        return inDoorway(walls, x, y, 1, 0, 1) ? { left: 0, right: T, top: T - (profiles?.get(`${x - 1},${y}`)?.body ?? WALL_BODY).depth } : undefined
    }
    if (profiles?.get(`${x},${y}`)?.block) return undefined
    if (!n && !s && !e && !w) return { left: LONE_WALL.left, right: LONE_WALL.right, top: T - LONE_WALL.depth }
    const { left, right, depth } = profiles?.get(`${x},${y}`)?.body ?? WALL_BODY
    const run = !n && !s
    return { left: w || (run && e) ? 0 : left, right: e || (run && w) ? T : right, top: n ? 0 : T - depth }
}

/**
 * How far a pitched roof cell rises over its eave at a ground y: 45-degree slopes up to a ridge
 * halfway along its north-south run, which is where the art draws the ridge (the top row, or one row
 * down when the whole ridge drops there -- see pitchedRoofPiece).
 */
// Each roof cell's column (how far its roll runs up and down) and whether its top piece drops, per roof set: a cast samples
// the same cell many times, and working the pitched piece out afresh each time was a third of a castle's re-cast.
const roofColumns = new WeakMap<ReadonlySet<string>, Map<string, { top: number; bottom: number; drops: boolean }>>()

export const roofRise = (roofs: ReadonlySet<string>, x: number, y: number, groundY: number): number => {
    let columns = roofColumns.get(roofs)
    if (!columns) roofColumns.set(roofs, columns = new Map())
    const id = `${x},${y}`
    let column = columns.get(id)
    if (!column) {
        const isRoof = (cx: number, cy: number) => roofs.has(`${cx},${cy}`)
        let top = y, bottom = y
        while (isRoof(x, top - 1)) top--
        while (isRoof(x, bottom + 1)) bottom++
        column = { top, bottom, drops: bottom > top && pitchedRoofPiece(isRoof, x, top).startsWith("back.") }
        columns.set(id, column)
    }
    const { top, bottom, drops } = column
    const depth = bottom - top + 1
    if (depth === 1) return T / 4
    const ridge = (T / 2) * depth - (drops ? T : 0)
    const middle = ((top + bottom + 1) * T) / 2
    return Math.max(0, ridge * (1 - Math.abs(groundY - middle) / ((depth * T) / 2)))
}

const casters = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions): Caster[] => {
    const out: Caster[] = []
    const byFloor = new Map(stack.map(storey => [storey.floor, storey]))
    stack.forEach(storey => {
        if (storey.floor < receiver) return
        const base = (storey.floor - receiver) * options.rise
        const above = byFloor.get(storey.floor + 1)
        wallFootprints(storey.walls, storey.profiles).forEach(({ cell, ...rect }) => {
            // A block is as tall as it is; a wall carrying another storey's wall runs up into it; a top wall ends at its own art.
            const block = storey.profiles?.get(cell)?.block
            if (block) return out.push({ ...rect, low: base, high: base + block.height })
            out.push({ ...rect, low: base, high: base + (above?.walls.has(cell) ? options.rise : WALL_TOP) })
        })
        // A slab ends flush with the outer face of a wall on its edge (its own storey's, or the one it rests on): a
        // tower's floors reaching to the cells' edges cast a step past its walls' shadow at every storey.
        const below = byFloor.get(storey.floor - 1)
        const open = (x: number, y: number) => !storey.surface.has(`${x},${y}`)
        if (storey.floor > receiver) storey.slab.forEach(cell => {
            const [x, y] = parse(cell)
            const wall = wallExtent(storey.walls, storey.profiles, x, y) ?? (below && wallExtent(below.walls, below.profiles, x, y))
            const left = wall && open(x - 1, y) ? wall.left : 0, right = wall && open(x + 1, y) ? wall.right : T
            const top = wall && open(x, y - 1) ? wall.top : 0
            out.push({ x0: x * T + left, y0: y * T + top, x1: x * T + right, y1: y * T + T, low: base, high: base })
        })
        storey.roofs.forEach(cell => {
            const [x, y] = parse(cell)
            const pitched = storey.pitched.has(cell)
            // A pitched cell in quarter strips, so a gable end casts its point.
            const strip = pitched ? T / 4 : T
            for (let s = 0; s < T; s += strip) {
                const h = base + options.roofLift + (pitched ? roofRise(storey.roofs, x, y, y * T + s + strip / 2) : 0)
                out.push({ x0: x * T, y0: y * T + s, x1: x * T + T, y1: y * T + s + strip, low: h, high: h })
            }
        })
    })
    return out
}

/** The convex hull of points given as flat pairs (monotone chain), as flat pairs. */
export const hull = (points: number[]): ShadowPolygon => {
    const pts: [number, number][] = []
    for (let i = 0; i < points.length; i += 2) pts.push([points[i], points[i + 1]])
    pts.sort((a, b) => a[0] - b[0] || a[1] - b[1])
    const cross = (o: number[], a: number[], b: number[]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
    const lower: [number, number][] = [], upper: [number, number][] = []
    for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
        lower.push(p)
    }
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i]
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
        upper.push(p)
    }
    return [...lower.slice(0, -1), ...upper.slice(0, -1)].flat()
}

/** A caster's footprint swept along the sun from its lowest point to its highest. */
const sweep = ({ x0, y0, x1, y1, low, high }: Caster, sun: { x: number; y: number }): ShadowPolygon => {
    const at = (h: number) => {
        const dx = sun.x * h, dy = sun.y * h
        return [x0 + dx, y0 + dy, x1 + dx, y0 + dy, x1 + dx, y1 + dy, x0 + dx, y1 + dy]
    }
    return low === high ? at(low) : hull([...at(low), ...at(high)])
}

/** A convex polygon cut to an axis-aligned rectangle (Sutherland-Hodgman); empty when nothing is left. */
export const clipToRect = (polygon: ShadowPolygon, x0: number, y0: number, x1: number, y1: number): ShadowPolygon => {
    let points = polygon
    const edges: [(x: number, y: number) => boolean, (ax: number, ay: number, bx: number, by: number) => [number, number]][] = [
        [(x) => x >= x0, (ax, ay, bx, by) => [x0, ay + ((x0 - ax) / (bx - ax)) * (by - ay)]],
        [(x) => x <= x1, (ax, ay, bx, by) => [x1, ay + ((x1 - ax) / (bx - ax)) * (by - ay)]],
        [(_, y) => y >= y0, (ax, ay, bx, by) => [ax + ((y0 - ay) / (by - ay)) * (bx - ax), y0]],
        [(_, y) => y <= y1, (ax, ay, bx, by) => [ax + ((y1 - ay) / (by - ay)) * (bx - ax), y1]],
    ]
    for (const [inside, cross] of edges) {
        const out: number[] = []
        const n = points.length / 2
        for (let i = 0; i < n; i++) {
            const ax = points[i * 2], ay = points[i * 2 + 1]
            const j = (i + 1) % n
            const bx = points[j * 2], by = points[j * 2 + 1]
            const aIn = inside(ax, ay), bIn = inside(bx, by)
            if (aIn) out.push(ax, ay)
            if (aIn !== bIn) out.push(...cross(ax, ay, bx, by))
        }
        points = out
        if (points.length < 6) return []
    }
    // A polygon touching the rectangle only along its edge leaves a sliver with no area.
    let area = 0
    for (let i = 0; i < points.length; i += 2) {
        const j = (i + 2) % points.length
        area += points[i] * points[j + 1] - points[j] * points[i + 1]
    }
    return Math.abs(area) > 1e-6 ? points : []
}

/**
 * Cuts polygons to where storey `receiver` of `stack` can take shade: the ground everywhere, an upper
 * storey where its floor art is drawn, the roofs below it where it has no floor. `exemptIndoors` keeps
 * floors under a roof or the storey above clear (options.indoors false, for sun shadows only).
 */
/** `rects` with every part `cuts` covers taken out, as rectangles. */
const subtractRects = (rects: FloorRect[], cuts: FloorRect[]): FloorRect[] => cuts.reduce((left, cut) => left.flatMap(rect => {
    if (cut.x0 >= rect.x1 || cut.x1 <= rect.x0 || cut.y0 >= rect.y1 || cut.y1 <= rect.y0) return [rect]
    const out: FloorRect[] = []
    if (rect.y0 < cut.y0) out.push({ ...rect, y1: cut.y0 })
    if (cut.y1 < rect.y1) out.push({ ...rect, y0: cut.y1 })
    const y0 = Math.max(rect.y0, cut.y0), y1 = Math.min(rect.y1, cut.y1)
    if (rect.x0 < cut.x0) out.push({ x0: rect.x0, y0, x1: cut.x0, y1 })
    if (cut.x1 < rect.x1) out.push({ x0: cut.x1, y0, x1: rect.x1, y1 })
    return out
}), rects)

/** The widest gap in a run of walls that still closes a room: a keep's or a gate's three-cell doorway. */
export const DOORWAY_SPAN = 3

/**
 * Which of a storey's `covered` cells are indoors, kept out of the sun's shade: those its walls close off. Covered
 * ground open at a side to the `sky` round it -- the bailey's edge under a rampart, a porch -- is out of doors, in the
 * shade of what covers it: seen past the overhang's edge, it was a lit band between the overhang and its shadow. A
 * room's doorway (a gap of up to DOORWAY_SPAN cells in a run of walls) closes it as its walls do. `arched`: cells the
 * storey above cuts an arch over (its `undrawn`).
 */
const indoorCells = (self: ShadowStorey, covered: ReadonlySet<string>, sky: (x: number, y: number) => boolean,
    arched?: ReadonlySet<string>): Set<string> => {
    const wall = (x: number, y: number) => self.walls.has(`${x},${y}`)
    const shut = (x: number, y: number): boolean => wall(x, y) ||
        inDoorway(self.walls, x, y, 1, 0, DOORWAY_SPAN) || inDoorway(self.walls, x, y, 0, 1, DOORWAY_SPAN)
    const stands = (x: number, y: number) => self.ground || self.surface.has(`${x},${y}`)
    const sides = [[1, 0], [-1, 0], [0, 1], [0, -1]]
    const open = new Set<string>()
    const pending: [number, number][] = []
    const reach = (x: number, y: number) => {
        const key = `${x},${y}`
        if (open.has(key) || shut(x, y) || !stands(x, y)) return
        open.add(key)
        pending.push([x, y])
    }
    covered.forEach(key => {
        const [x, y] = parse(key)
        if (sides.some(([dx, dy]) => !covered.has(`${x + dx},${y + dy}`) && sky(x + dx, y + dy) && stands(x + dx, y + dy) &&
            !shut(x + dx, y + dy))) reach(x, y)
    })
    while (pending.length) {
        const [x, y] = pending.pop()!
        sides.forEach(([dx, dy]) => { if (covered.has(`${x + dx},${y + dy}`)) reach(x + dx, y + dy) })
    }
    // A doorway with out of doors on both sides -- a castle's gate, under the arch, between the field and the bailey -- is
    // out of doors too, in the shade of what spans it. So is one under an arch out of doors on one side (a tower's door):
    // the threshold under the vault, as dark as the room seen through the arch (the storey above shades that). Any other
    // opening on a room stays shut.
    const outside = (x: number, y: number) => open.has(`${x},${y}`) ||
        (!covered.has(`${x},${y}`) && sky(x, y) && stands(x, y) && !shut(x, y))
    const through = (x: number, y: number, dx: number, dy: number) => inDoorway(self.walls, x, y, dx, dy, DOORWAY_SPAN) &&
        (arched?.has(`${x},${y}`) ? outside(x - dy, y - dx) || outside(x + dy, y + dx) : outside(x - dy, y - dx) && outside(x + dy, y + dx))
    const gates = [...covered].filter(key => {
        const [x, y] = parse(key)
        return !open.has(key) && !wall(x, y) && stands(x, y) && (through(x, y, 1, 0) || through(x, y, 0, 1))
    })
    gates.forEach(key => open.add(key))
    return open.size ? new Set([...covered].filter(key => !open.has(key))) : new Set(covered)
}

/** Where storey `receiver` can take shade, cell by cell (see receiverClip); `unclipped` when everywhere. */
const receiverCells = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions, exemptIndoors: boolean) => {
    const self = stack.find(storey => storey.floor === receiver)
    if (!self) return undefined
    const upper = stack.find(storey => storey.floor === receiver + 1)
    const hasFloor = (x: number, y: number) => self.surface.has(`${x},${y}`)
    // Under the storey above: its floor, and its stairwells (a stair stands over them, not the sky). A real
    // opening in that floor still lets the sun through.
    const covered = exemptIndoors && !options.indoors ? new Set([...self.roofs, ...(upper?.slab ?? []), ...(upper?.surface ?? [])]) : undefined
    // Open to the sky: nothing over it on any storey (an opening under a higher floor or roof is not).
    const above = stack.filter(storey => storey.floor > receiver)
    const sky = (x: number, y: number) => {
        const key = `${x},${y}`
        return !self.roofs.has(key) && !above.some(storey => storey.surface.has(key) || storey.slab.has(key) || storey.roofs.has(key))
    }
    const exempt = covered && indoorCells(self, covered, sky, upper?.undrawn)
    // The parts of a cell that receive: the whole cell on the ground, the drawn floor upstairs.
    // A roof below is drawn its uplift over its own storey, a little above this storey's plane.
    const roofShift = options.roofLift - options.rise
    const surfaceAt = (x: number, y: number): FloorRect[] => {
        const key = `${x},${y}`
        if (self.ground) return [{ x0: x * T, y0: y * T, x1: x * T + T, y1: y * T + T }]
        if (self.surface.has(key)) return self.undrawn?.has(key) ? [] : drawnFloorRects(x, y, hasFloor)
        if (self.parapets?.has(key)) return [{ x0: x * T, y0: y * T, x1: x * T + T, y1: y * T + T }]
        return self.roofsBelow?.has(key) ? [{ x0: x * T, y0: y * T - roofShift, x1: x * T + T, y1: y * T + T - roofShift }] : []
    }
    const upperFloor = (x: number, y: number) => !!upper?.surface.has(`${x},${y}`)
    // Overlapping shadows ask about the same cells again and again: each cell is worked out once a cast (read-only after).
    const received = new Map<string, FloorRect[]>()
    const receiving = (x: number, y: number): FloorRect[] => {
        const key = `${x},${y}`
        let rects = received.get(key)
        if (!rects) received.set(key, rects = receivingAt(x, y))
        return rects
    }
    const receivingAt = (x: number, y: number): FloorRect[] => {
        if (!exempt?.has(`${x},${y}`)) return surfaceAt(x, y)
        // Indoors is what a roof covers, all of its cell, or the storey above where its floor is drawn: that stops short of
        // its open edges (floor_art.ts), and the strip it leaves is out under the sky (the bailey's edge under a rampart's).
        const cell = { x0: x * T, y0: y * T, x1: x * T + T, y1: y * T + T }
        let cover = self.roofs.has(`${x},${y}`) ? [cell] : upperFloor(x, y) ? drawnFloorRects(x, y, upperFloor) : []
        // So is a wall's cell past the wall's outer face, under a roof too (the ground west of a tower's west wall).
        // Under the wall's outer half too, so the shadow's soft edge lies under its art as it does round a wall in the open.
        // A doorway's floor shows: there it stops at the run's face.
        const wall = wallExtent(self.walls, self.profiles, x, y)
        if (wall) {
            const middle = self.walls.has(`${x},${y}`) ? (wall.left + wall.right) / 2 : undefined
            if (wall.left > 0 && !exempt.has(`${x - 1},${y}`)) cover = subtractRects(cover, [{ ...cell, x1: x * T + (middle ?? wall.left) }])
            if (wall.right < T && !exempt.has(`${x + 1},${y}`)) cover = subtractRects(cover, [{ ...cell, x0: x * T + (middle ?? wall.right) }])
        }
        return subtractRects(surfaceAt(x, y), cover)
    }
    /** Whether cell (x, y) receives on a roof of the storey below (no floor of this storey's there). */
    const onRoofBelow = (x: number, y: number): boolean => {
        const key = `${x},${y}`
        return !self.ground && !self.surface.has(key) && !self.parapets?.has(key) && !!self.roofsBelow?.has(key)
    }
    return { receiving, onRoofBelow, unclipped: self.ground && !exempt, exempt }
}

/** The cells of storey `receiver` kept out of the sun's shade as indoors (none when interiors take it: `options.indoors`). */
export const indoorShadeCells = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions): ReadonlySet<string> =>
    receiverCells(stack, receiver, options, true)?.exempt || new Set<string>()

/**
 * Roof cells below a storey whose shade is kept apart from the rest (`out`): the roof's own storey draws the shade on
 * them (castRoofShadows), so the storey above takes it as its own (bodies walking there) without drawing it again.
 */
export interface RoofShadeApart { cells: ReadonlySet<string>; out: ShadowPolygon[] }

/**
 * Where storey `receiver` takes the sun's shade inside a box of its pixels, as rectangles joined along
 * each row; undefined where it takes it everywhere (open ground). For the shadows no polygon can be cut
 * to: plants', entity tiles' and furniture's stamped silhouettes, masked to these when they are baked.
 */
export const sunReceivingRects = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions) => {
    const cells = receiverCells(stack, receiver, options, true)
    if (!cells || cells.unclipped) return undefined
    return (x0: number, y0: number, x1: number, y1: number): FloorRect[] => {
        const out: FloorRect[] = []
        for (let cy = Math.floor(y0 / T) - 1; cy <= Math.ceil(y1 / T) + 1; cy++) {
            for (let cx = Math.floor(x0 / T); cx <= Math.ceil(x1 / T); cx++) {
                for (const rect of cells.receiving(cx, cy)) {
                    const last = out[out.length - 1]
                    if (last && last.y0 === rect.y0 && last.y1 === rect.y1 && last.x1 === rect.x0) last.x1 = rect.x1
                    else out.push({ ...rect })
                }
            }
        }
        return out
    }
}

const receiverClip = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions, exemptIndoors: boolean,
    apart?: RoofShadeApart) => {
    const cells = receiverCells(stack, receiver, options, exemptIndoors)
    if (!cells) return undefined
    const { receiving, onRoofBelow, unclipped } = cells
    const isApart = (x: number, y: number) => !!apart?.cells.has(`${x},${y}`) && onRoofBelow(x, y)
    const whole = (rects: FloorRect[]) => rects.length === 1 && rects[0].x1 - rects[0].x0 === T && rects[0].y1 - rects[0].y0 === T
    return (polygon: ShadowPolygon, out: ShadowPolygon[]): void => {
        if (unclipped) { out.push(polygon); return }
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (let i = 0; i < polygon.length; i += 2) {
            minX = Math.min(minX, polygon[i]); maxX = Math.max(maxX, polygon[i])
            minY = Math.min(minY, polygon[i + 1]); maxY = Math.max(maxY, polygon[i + 1])
        }
        const cx0 = Math.floor(minX / T), cx1 = Math.ceil(maxX / T) - 1
        const cy0 = Math.floor(minY / T), cy1 = Math.ceil(maxY / T) - 1
        const rows: (FloorRect & { apart: boolean })[][] = []
        let all = true, anyApart = false
        for (let cy = cy0; cy <= cy1; cy++) {
            const row: (FloorRect & { apart: boolean })[] = []
            for (let cx = cx0; cx <= cx1; cx++) {
                const rects = receiving(cx, cy)
                const set = isApart(cx, cy)
                all = all && whole(rects)
                anyApart = anyApart || (set && rects.length > 0)
                rects.forEach(rect => row.push({ ...rect, apart: set }))
            }
            rows.push(row)
        }
        if (all && !anyApart) { out.push(polygon); return }
        // Cut it to the receiving rectangles, joined along each band of each row.
        rows.forEach(row => {
            const bands = new Map<string, (FloorRect & { apart: boolean })[]>()
            row.forEach(rect => {
                const key = `${rect.apart}:${rect.y0}:${rect.y1}`
                const band = bands.get(key)
                const last = band?.[band.length - 1]
                if (last && last.x1 === rect.x0) last.x1 = rect.x1
                else if (band) band.push({ ...rect })
                else bands.set(key, [{ ...rect }])
            })
            bands.forEach(band => band.forEach(({ x0, y0, x1, y1, apart: set }) => {
                const piece = clipToRect(polygon, x0, y0, x1, y1)
                if (piece.length) (set ? apart!.out : out).push(piece)
            }))
        })
    }
}

/**
 * The shade on storey `receiver` of `stack` (every storey of the building that is known, any order),
 * as convex polygons in that storey's pixels, already cut to where it can land. Draw them as one solid
 * union and apply the shadow's alpha once, so overlaps never darken twice. `apart`: the shade on those roofs below
 * goes there instead.
 */
export const castBuildingShadows = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions,
    apart?: RoofShadeApart): ShadowPolygon[] => {
    const clip = receiverClip(stack, receiver, options, true, apart)
    if (!clip) return []
    const polygons: ShadowPolygon[] = []
    casters(stack, receiver, options).forEach(caster => clip(sweep(caster, options.sun), polygons))
    return polygons
}

/**
 * The sun's shade on storey `roofFloor`'s roof (`cells`), in that storey's pixels, as it is drawn over the roof's art
 * (`roofLift` above its cells). The storeys above cast as onto the open ground round the building, so a shadow lying
 * across that ground carries on over the roof unbroken at its edges. A roof caught at its real height would show the
 * shadow pulled back toward its caster, out of line with the ground beside it, and on a pitched roof only on the slope
 * facing away from the camera. Only what rises above the roof's eave casts: a fence behind the house never reaches it.
 * No shade where the floor of the storey above covers the roof.
 */
export const castRoofShadows = (stack: readonly ShadowStorey[], roofFloor: number, cells: ReadonlySet<string>,
    options: BuildingShadowOptions): ShadowPolygon[] => {
    const covered = stack.find(storey => storey.floor === roofFloor + 1)?.surface
    const roof = (x: number, y: number) => cells.has(`${x},${y}`) && !covered?.has(`${x},${y}`)
    // Heights from the ground; the roof storey stands `lift` above it, drawn that much higher, so a point of the ground
    // lies `lift` further down in its pixels.
    const lift = roofFloor * options.rise, eave = lift + options.roofLift
    const out: ShadowPolygon[] = []
    casters(stack.filter(storey => storey.floor > roofFloor), 0, options).forEach(caster => {
        if (caster.high <= eave) return
        const polygon = sweep({ ...caster, low: Math.max(caster.low, eave) }, options.sun).map((v, i) => (i % 2 ? v + lift : v))
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (let i = 0; i < polygon.length; i += 2) {
            minX = Math.min(minX, polygon[i]); maxX = Math.max(maxX, polygon[i])
            minY = Math.min(minY, polygon[i + 1]); maxY = Math.max(maxY, polygon[i + 1])
        }
        // Each row of roof cells is drawn `roofLift` up: cut to its runs.
        const cx0 = Math.floor(minX / T), cx1 = Math.ceil(maxX / T) - 1
        for (let y = Math.floor((minY + options.roofLift) / T); y < Math.ceil((maxY + options.roofLift) / T); y++) {
            for (let x = cx0; x <= cx1; x++) {
                if (!roof(x, y)) continue
                let end = x
                while (end < cx1 && roof(end + 1, y)) end++
                const piece = clipToRect(polygon, x * T, y * T - options.roofLift, (end + 1) * T, (y + 1) * T - options.roofLift)
                if (piece.length) out.push(piece)
                x = end
            }
        }
    })
    return out
}

/**
 * Contact shade: the floor darkens where anything stands on it, the way a corner gathers shadow out of
 * the sun's reach too. A storey's own walls, each footprint grown by `reach` px, cut to where that
 * storey can take shade (indoors included: this is no sunlight). Baked heavily softened, these become a
 * soft band along every wall's foot and round every post. `extra` (entity tiles' feet) is cut the same way.
 */
export const castContactShadows = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions,
    reach: number, extra: readonly ShadowPolygon[] = []): ShadowPolygon[] => {
    const self = stack.find(storey => storey.floor === receiver)
    const clip = receiverClip(stack, receiver, options, false)
    if (!self || !clip) return []
    const polygons: ShadowPolygon[] = []
    wallFootprints(self.walls, self.profiles).forEach(({ x0, y0, x1, y1 }) =>
        clip([x0 - reach, y0 - reach, x1 + reach, y0 - reach, x1 + reach, y1 + reach, x0 - reach, y1 + reach], polygons))
    extra.forEach(polygon => clip(polygon, polygons))
    return polygons
}

/** Whether a point lies in a convex polygon (either winding). */
/**
 * The shadow an upper storey's posts throw on their own level, as if a floor were there to take it, drawn where that
 * level is drawn: on the ground's layer, so it joins the ground's shadow and never darkens twice. A stack of posts
 * over open ground then shows each storey's piece with a shadow of its own beside it, where it would otherwise be one
 * long pole over one long shadow. Only posts (a north-south run's walls, theirs or the run they stand on), and only
 * where no floor up there takes their real shadow. It is for the eye alone: nobody below stands in it.
 */
export const elevatedPostShadows = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions): ShadowPolygon[] => {
    if (!stack.find(storey => storey.floor === receiver)?.ground) return []
    const byFloor = new Map(stack.map(storey => [storey.floor, storey]))
    const out: ShadowPolygon[] = []
    stack.forEach(storey => {
        if (storey.floor <= receiver) return
        const lift = (storey.floor - receiver) * options.rise
        const above = byFloor.get(storey.floor + 1)
        const below = byFloor.get(storey.floor - 1)
        const has = (s: ShadowStorey | undefined, x: number, y: number) => !!s?.walls.has(`${x},${y}`)
        wallFootprints(storey.walls, storey.profiles).forEach(({ cell, ...rect }) => {
            const [x, y] = parse(cell)
            if (storey.profiles?.get(cell)?.block || has(storey, x - 1, y) || has(storey, x + 1, y)) return
            const column = has(storey, x, y - 1) || has(storey, x, y + 1) ||
                (has(below, x, y) && (has(below, x, y - 1) || has(below, x, y + 1)))
            // The sun throws it west and a little south: a floor on those cells up there takes it already.
            const caught = [[x, y], [x - 1, y], [x, y + 1], [x - 1, y + 1]].some(([cx, cy]) => storey.surface.has(`${cx},${cy}`))
            if (!column || caught) return
            const polygon = sweep({ ...rect, low: 0, high: above?.walls.has(cell) ? options.rise : WALL_TOP }, options.sun)
            out.push(polygon.map((v, i) => (i % 2 ? v - lift : v)))
        })
    })
    return out
}

export const insidePolygon = (polygon: ShadowPolygon, x: number, y: number): boolean => {
    let sign = 0
    const n = polygon.length / 2
    for (let i = 0; i < n; i++) {
        const ax = polygon[i * 2], ay = polygon[i * 2 + 1]
        const j = (i + 1) % n
        const bx = polygon[j * 2], by = polygon[j * 2 + 1]
        const cross = (bx - ax) * (y - ay) - (by - ay) * (x - ax)
        if (cross === 0) continue
        if (sign === 0) sign = Math.sign(cross)
        else if (Math.sign(cross) !== sign) return false
    }
    // A polygon with no area (every vertex on one line) holds no point.
    return sign !== 0
}

/**
 * A key for a set of polygons that changes when any of them moves by a quarter pixel: two 32-bit
 * hashes of every vertex, rounded. Compared on every edit of a room, where a string of the vertices
 * themselves (tens of thousands of numbers once a forest casts) was most of the cost and its garbage.
 */
// A wall material's shadow profile by identity: the same definition object casts the same way.
const profileIds = new WeakMap<object, number>()
let nextProfileId = 0
const profileId = (shadow: WallShadow): number => {
    let id = profileIds.get(shadow)
    if (id === undefined) profileIds.set(shadow, id = ++nextProfileId)
    return id
}

/**
 * Everything a stack's buildings cast from (this model is pure: equal stacks cast equal shadows), as one exact key.
 * A storey's map version moves on every tile change -- a swing at long grass, a cut tree -- and re-casting a castle's
 * walls and roofs on each cost ~25ms a storey, for shadows the change could not have moved.
 */
export const shadowStackKey = (stack: readonly ShadowStorey[]): string => stack.map(storey => {
    const cells = (set?: ReadonlySet<string>) => set ? [...set].join(";") : "-"
    const profiles = storey.profiles ? [...storey.profiles].map(([cell, shadow]) => `${cell}=${profileId(shadow)}`).join(";") : "-"
    return [storey.floor, storey.ground ? 1 : 0, cells(storey.walls), cells(storey.surface), cells(storey.undrawn), cells(storey.slab),
        cells(storey.roofs), cells(storey.pitched), cells(storey.roofsBelow), profiles, cells(storey.parapets)].join("|")
}).join("#")

export const polygonsKey = (polygons: readonly ShadowPolygon[]): string => {
    let a = 0x811c9dc5, b = 0x9e3779b9, count = 0
    for (const polygon of polygons) {
        for (let i = 0; i < polygon.length; i++) {
            const v = Math.round(polygon[i] * 4)
            a = Math.imul(a ^ v, 0x01000193)
            b = Math.imul(b ^ (v + i), 0x85ebca6b) ^ (b >>> 13)
        }
        // Where one polygon ends and the next begins.
        a = Math.imul(a ^ polygon.length, 0x01000193)
        count += polygon.length
    }
    return `${polygons.length}:${count}:${a >>> 0}:${b >>> 0}`
}

/** Whether a point lies in any of the polygons. */
export const shadedAt = (polygons: readonly ShadowPolygon[], x: number, y: number): boolean =>
    polygons.some(polygon => insidePolygon(polygon, x, y))
